import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { axiosInstance } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  CreditCard, 
  Check, 
  AlertCircle,
  DollarSign,
  Clock,
  ExternalLink,
  Loader2,
  RefreshCw,
  AtSign,
  ImagePlus,
  Trash2,
  ShieldCheck
} from "lucide-react";
import { toast } from "sonner";

const isUsernameEmoji = (codepoint) => (
  (codepoint >= 0x2190 && codepoint <= 0x21FF)
  || (codepoint >= 0x2300 && codepoint <= 0x23FF)
  || (codepoint >= 0x2600 && codepoint <= 0x27BF)
  || (codepoint >= 0x2B00 && codepoint <= 0x2BFF)
  || (codepoint >= 0x3030 && codepoint <= 0x303D)
  || (codepoint >= 0x3297 && codepoint <= 0x3299)
  || (codepoint >= 0x1F000 && codepoint <= 0x1FAFF)
);

const isAllowedUsernameCharacter = (character) => {
  if (/^[A-Za-z0-9._]$/.test(character)) return true;
  const codepoint = character.codePointAt(0);
  return [0x200D, 0x20E3, 0xFE0E, 0xFE0F].includes(codepoint) || isUsernameEmoji(codepoint);
};

const prepareProfilePhoto = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith("image/")) {
    reject(new Error("Choose a JPEG, PNG, or WebP image"));
    return;
  }
  if (file.size > 8 * 1024 * 1024) {
    reject(new Error("Choose an image smaller than 8 MB"));
    return;
  }

  const image = new Image();
  const objectUrl = URL.createObjectURL(file);
  image.onload = () => {
    const crop = Math.min(image.naturalWidth, image.naturalHeight);
    const sourceX = Math.round((image.naturalWidth - crop) / 2);
    const sourceY = Math.round((image.naturalHeight - crop) / 2);
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    canvas.getContext("2d").drawImage(image, sourceX, sourceY, crop, crop, 0, 0, 512, 512);
    URL.revokeObjectURL(objectUrl);
    resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("That image could not be opened"));
  };
  image.src = objectUrl;
});

const CreatorSettings = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [username, setUsername] = useState(user?.username || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [pictureDirty, setPictureDirty] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    setUsername(user?.username || "");
    if (!pictureDirty) setPicture(user?.picture || "");
  }, [user?.username, user?.picture, pictureDirty]);

  useEffect(() => {
    // Check for Stripe return params
    const stripeSuccess = searchParams.get("stripe_success");
    const stripeRefresh = searchParams.get("stripe_refresh");
    
    if (stripeSuccess === "true") {
      toast.success("Stripe account connected successfully!");
      // Clear the URL params
      navigate("/creator/settings", { replace: true });
    } else if (stripeRefresh === "true") {
      toast.info("Please complete your Stripe onboarding to enable payouts.");
      navigate("/creator/settings", { replace: true });
    }
    
    fetchData();
  }, [searchParams, navigate]);

  const fetchData = async () => {
    try {
      const [stripeRes, earningsRes, payoutsRes] = await Promise.all([
        axiosInstance.get("/stripe/connect/status"),
        axiosInstance.get("/creator/earnings"),
        axiosInstance.get("/creator/payouts")
      ]);
      
      setStripeStatus(stripeRes.data);
      setEarnings(earningsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load payout settings");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      // First create/get the Stripe account
      await axiosInstance.post("/stripe/connect/create-account");
      
      // Then get the onboarding link
      const response = await axiosInstance.post("/stripe/connect/onboarding-link", null, {
        params: { origin_url: window.location.origin }
      });
      
      // Redirect to Stripe hosted onboarding
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error connecting Stripe:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Stripe");
      setConnectingStripe(false);
    }
  };

  const handleManageStripe = async () => {
    setConnectingStripe(true);
    try {
      // Get onboarding link to continue setup or manage account
      const response = await axiosInstance.post("/stripe/connect/onboarding-link", null, {
        params: { origin_url: window.location.origin }
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error getting Stripe link:", error);
      toast.error(error.response?.data?.detail || "Failed to get Stripe dashboard link");
      setConnectingStripe(false);
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    const normalizedUsername = username.trim().replace(/^@+/, "");
    if (normalizedUsername) {
      const characters = Array.from(normalizedUsername);
      if (characters.length < 3 || characters.length > 30 || !characters.every(isAllowedUsernameCharacter)) {
        toast.error("Username must be 3–30 characters using letters, numbers, periods, underscores, or emoji");
        return;
      }
    }

    setSavingProfile(true);
    try {
      const response = await axiosInstance.put("/auth/profile", {
        username: normalizedUsername || null,
        ...(pictureDirty ? { picture: picture || null } : {}),
      });
      setUsername(response.data.username || "");
      setPicture(response.data.picture || "");
      setPictureDirty(false);
      onUserUpdate?.(response.data);
      toast.success("Public profile updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update username");
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  const availableBalance = earnings?.creator_earnings || 0;
  const pendingAmount = earnings?.pending_payout || 0;
  const paidOutAmount = earnings?.paid_out || 0;

  const isStripeFullyConnected = stripeStatus?.connected && stripeStatus?.payouts_enabled;
  const isStripePartiallyConnected = stripeStatus?.connected && !stripeStatus?.payouts_enabled;

  return (
    <div className="min-h-screen bg-[#0f0f0f]" data-testid="creator-settings">
      <Navbar user={user} onLogout={onLogout} isCreator={true} />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-20">
        <h1 className="text-white text-4xl font-black mb-8">Profile &amp; Payout Settings</h1>

        <form onSubmit={handleSaveProfile} className="mb-8 rounded-xl border border-blue-500/20 bg-gray-900/50 p-6" data-testid="profile-settings-form">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/15 text-blue-300">
              <AtSign className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Public Profile</h2>
              <p className="text-sm text-gray-400">This username appears on your live events.</p>
            </div>
          </div>

          <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-white/10 bg-black/20 p-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-blue-400/40 bg-blue-500/15 text-2xl font-black text-blue-200">
              {picture ? (
                <img src={picture} alt="Profile preview" className="h-full w-full object-cover" />
              ) : (
                (username || user?.name || "U").charAt(0).toUpperCase()
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white">Profile photo</p>
              <p className="mb-3 text-xs text-gray-400">Shown beside your name in live event conversations.</p>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-blue-500">
                  {processingPhoto ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
                  Upload photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    disabled={processingPhoto}
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (!file) return;
                      setProcessingPhoto(true);
                      try {
                        setPicture(await prepareProfilePhoto(file));
                        setPictureDirty(true);
                      } catch (error) {
                        toast.error(error.message);
                      } finally {
                        setProcessingPhoto(false);
                      }
                    }}
                    data-testid="profile-photo-input"
                  />
                </label>
                {picture && (
                  <button type="button" onClick={() => { setPicture(""); setPictureDirty(true); }} className="inline-flex items-center rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold text-gray-300 hover:bg-white/5">
                    <Trash2 className="mr-2 h-4 w-4" />Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <label htmlFor="profile-username" className="mb-2 block text-sm font-semibold text-gray-300">Username</label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                id="profile-username"
                value={username}
                onChange={(event) => setUsername(event.target.value.replace(/^@+/, ""))}
                minLength={3}
                maxLength={60}
                autoComplete="username"
                placeholder="your.username"
                className="border-gray-700 bg-gray-950 pl-9 text-white"
                data-testid="profile-username-input"
              />
            </div>
            <Button
              type="submit"
              disabled={savingProfile || processingPhoto || (!pictureDirty && username.trim() === (user?.username || ""))}
              className="bg-blue-600 text-white hover:bg-blue-500 sm:min-w-36"
              data-testid="save-profile-button"
            >
              {savingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Profile
            </Button>
          </div>
          <p className="mt-2 text-xs text-gray-500">3–30 characters. Letters, numbers, periods, underscores, and emoji are welcome. Leave blank to use your account name.</p>
        </form>

        <section className="mb-8 rounded-xl border border-white/10 bg-gray-900/50 p-6" data-testid="account-privacy-settings">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-blue-500/15 p-2.5 text-blue-300">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Account &amp; Privacy</h2>
                <p className="mt-1 text-sm text-gray-400">
                  Manage connected sign-in methods or permanently delete your account and associated data.
                </p>
              </div>
            </div>
            <Button type="button" onClick={() => navigate("/settings/account")} className="bg-gray-700 hover:bg-gray-600">
              Manage Account
            </Button>
          </div>
        </section>
        
        {/* Balance Overview */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900/50 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-gray-400 text-sm">Total Earnings</span>
            </div>
            <p className="text-white text-3xl font-bold">${availableBalance.toFixed(2)}</p>
          </div>
          
          <div className="bg-gray-900/50 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <span className="text-gray-400 text-sm">Pending Payouts</span>
            </div>
            <p className="text-white text-3xl font-bold">${pendingAmount.toFixed(2)}</p>
          </div>
          
          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="paid-out-card">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-gray-400 text-sm">Paid Out</span>
            </div>
            <p className="text-white text-3xl font-bold">${paidOutAmount.toFixed(2)}</p>
          </div>

        </div>

        {/* Stripe Connect Section */}
        <div className="bg-gray-900/50 rounded-lg p-6 mb-8">
          <h2 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Account
          </h2>
          
          {isStripeFullyConnected ? (
            // Fully connected and payouts enabled
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Stripe Connected</p>
                    <p className="text-gray-400 text-sm">
                      Payouts enabled • Account ID: •••• {stripeStatus.account_id?.slice(-4)}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleManageStripe}
                  disabled={connectingStripe}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  data-testid="manage-stripe-button"
                >
                  {connectingStripe ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Manage Account
                </Button>
              </div>
              {/* Stripe Dashboard Link */}
              <a
                href="https://dashboard.stripe.com/express"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#635BFF]/10 hover:bg-[#635BFF]/20 border border-[#635BFF]/30 rounded-lg text-[#A8A3FF] hover:text-white transition-colors"
                data-testid="stripe-dashboard-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                <span className="font-medium">Open Stripe Dashboard</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ) : isStripePartiallyConnected ? (
            // Connected but onboarding incomplete
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-600/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-white font-semibold">Setup Incomplete</p>
                  <p className="text-gray-400 text-sm">
                    Complete your Stripe account setup to receive payouts
                  </p>
                  {stripeStatus.requirements?.length > 0 && (
                    <p className="text-yellow-500 text-xs mt-1">
                      {stripeStatus.requirements.length} item(s) pending
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleManageStripe}
                disabled={connectingStripe}
                className="bg-yellow-600 hover:bg-yellow-700 flex items-center gap-2"
                data-testid="complete-stripe-setup-button"
              >
                {connectingStripe ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Complete Setup
              </Button>
            </div>
          ) : (
            // Not connected at all
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-white font-semibold">Connect Stripe to Get Paid</p>
                  <p className="text-gray-400 text-sm">
                    Set up your Stripe account to receive payouts from ticket sales
                  </p>
                </div>
              </div>
              <Button
                onClick={handleConnectStripe}
                disabled={connectingStripe}
                className="bg-[#635BFF] hover:bg-[#5349e8] flex items-center gap-2"
                data-testid="connect-stripe-button"
              >
                {connectingStripe ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Connect with Stripe
              </Button>
            </div>
          )}
          
          {/* Stripe branding */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <p className="text-gray-500 text-xs flex items-center gap-1">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
              Powered by Stripe • Secure, encrypted payments
            </p>
          </div>
        </div>

        {/* Info message if can't withdraw */}
        {!isStripeFullyConnected && (
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-8">
            <p className="text-blue-300 text-sm">
              Connect your Stripe account above to start receiving payouts from your ticket sales.
            </p>
          </div>
        )}


        {/* Payout History */}
        <div className="bg-gray-900/50 rounded-lg p-6">
          <h2 className="text-white text-xl font-bold mb-4">Payout History</h2>
          
          {payouts.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No payouts yet</p>
          ) : (
            <div className="space-y-3">
              {payouts.map((payout) => (
                <div 
                  key={payout.id}
                  className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg"
                  data-testid={`payout-${payout.id}`}
                >
                  <div>
                    <p className="text-white font-semibold">${payout.net_amount.toFixed(2)}</p>
                    <p className="text-gray-400 text-sm">
                      {new Date(payout.initiated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    payout.status === "completed" 
                      ? "bg-green-600/20 text-green-400"
                      : payout.status === "failed"
                      ? "bg-red-600/20 text-red-400"
                      : "bg-yellow-600/20 text-yellow-400"
                  }`}>
                    {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default CreatorSettings;
